// EAGER, hand-written WKB/EWKB structural helpers for the header sniffer. This file, spatialSniff.ts
// and sniffContainers.ts are the ONLY spatial-decode code the grid renderer graph may import — they
// MUST NOT import wkx/, any decoder, proj4, or maplibre (bundle isolation). This re-implements a
// minimal WKB/EWKB structure walk that COUNTS points without materialising any GeoJSON.

import type { SpatialContainer, SpatialDimensions, SpatialGeomType } from './types';

export type SniffFacts = {
  container: SpatialContainer; // widened to the full union so per-engine walkers (mysql-srid-wkb,
  // mssql-clr, sdo-object) can return their container without editing this shared type (Wave-3 scaffold)
  geomType: SpatialGeomType | 'Unknown';
  srid: number | null;
  dimensions: SpatialDimensions;
  pointCount: number | null; // null = value over MAX_SNIFF_WALK_BYTES (header facts only)
  byteLength: number;
};

export const HEADER_PREFIX_BYTES = 128; // enough for any container header + inner WKB header
const HEX_RE = /^[0-9a-fA-F]+$/;
const MIN_HEX = 10; // shortest plausible WKB header (byte order + 4-byte type) = 5 bytes
const SRID_FLAG = 0x20000000;
const M_FLAG = 0x40000000;
const Z_FLAG = 0x80000000;
const TYPE_MASK = 0x1fffffff;
// Index 0 is an unused filler (baseType 0 is never a valid geometry — geomName gates on 1..7).
const WKB_NAMES: SpatialGeomType[] =
  ['Point', 'Point', 'LineString', 'Polygon', 'MultiPoint', 'MultiLineString', 'MultiPolygon', 'GeometryCollection'];

export type HexInfo = { clean: string; byteLength: number };

export function hexInfo(value: unknown): HexInfo | null {
  if (typeof value !== 'string') return null;
  let s = value;
  if (s.startsWith('0x') || s.startsWith('0X') || s.startsWith('\\x')) s = s.slice(2);
  if (s.length < MIN_HEX || s.length % 2 !== 0) return null;
  return { clean: s, byteLength: s.length / 2 };
}

/** Converts up to `maxBytes` bytes of a hex string, validating only the converted portion (so a
 * huge over-cap value stays O(header), never O(value)). Returns null if the portion is not hex. */
export function hexToBytes(clean: string, maxBytes: number): Uint8Array | null {
  const n = Math.min(clean.length / 2, maxBytes);
  const portion = n * 2 === clean.length ? clean : clean.slice(0, n * 2);
  if (!HEX_RE.test(portion)) return null;
  const out = new Uint8Array(n);
  for (let i = 0; i < n; i += 1) out[i] = parseInt(clean.slice(i * 2, i * 2 + 2), 16);
  return out;
}

export function createReader(bytes: Uint8Array) {
  const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let pos = 0;
  let le = true;
  const need = (n: number) => {
    if (pos + n > bytes.byteLength) throw new Error('end of buffer');
  };
  return {
    byteOrder() {
      need(1);
      le = dv.getUint8(pos) === 1;
      pos += 1;
    },
    setLE(v: boolean) {
      le = v;
    },
    u32() {
      need(4);
      const v = dv.getUint32(pos, le);
      pos += 4;
      return v;
    },
    f64() {
      need(8);
      const v = dv.getFloat64(pos, le);
      pos += 8;
      return v;
    },
    skip(n: number) {
      need(n);
      pos += n;
    },
  };
}

export type Reader = ReturnType<typeof createReader>;
type WkbHeader = { baseType: number; hasZ: boolean; hasM: boolean; srid: number | null; ewkb: boolean };

export function dimsLabel(hasZ: boolean, hasM: boolean): SpatialDimensions {
  return hasZ && hasM ? 'XYZM' : hasZ ? 'XYZ' : hasM ? 'XYM' : 'XY';
}

export function geomName(baseType: number): SpatialGeomType | 'Unknown' | null {
  if (baseType >= 1 && baseType <= 7) return WKB_NAMES[baseType];
  if (baseType >= 8 && baseType <= 17) return 'Unknown'; // curves — header facts only
  return null;
}

function readWkbHeader(r: Reader, parentEwkb: boolean): WkbHeader {
  r.byteOrder();
  const raw = r.u32();
  const hasSrid = (raw & SRID_FLAG) !== 0;
  const zf = (raw & Z_FLAG) !== 0;
  const mf = (raw & M_FLAG) !== 0;
  const ewkb = hasSrid || zf || mf || parentEwkb;
  const srid = hasSrid ? r.u32() : null;
  const t = raw & TYPE_MASK;
  if (!ewkb) {
    if (t >= 3000 && t < 4000) return { baseType: t - 3000, hasZ: true, hasM: true, srid, ewkb };
    if (t >= 2000 && t < 3000) return { baseType: t - 2000, hasZ: false, hasM: true, srid, ewkb };
    if (t >= 1000 && t < 2000) return { baseType: t - 1000, hasZ: true, hasM: false, srid, ewkb };
    return { baseType: t, hasZ: false, hasM: false, srid, ewkb };
  }
  return { baseType: t, hasZ: zf, hasM: mf, srid, ewkb };
}

/** Counts points in a WKB body, consuming its bytes. Mirrors the wkx decoder's per-shape counting
 * (Point NaN→0; MultiPoint counts members; else sums vertices) so sniff and decode always agree. */
export function walkWkbBody(r: Reader, h: WkbHeader): number {
  const dpp = 2 + (h.hasZ ? 1 : 0) + (h.hasM ? 1 : 0);
  switch (h.baseType) {
    case 1: {
      let allNaN = true;
      for (let i = 0; i < dpp; i += 1) if (!Number.isNaN(r.f64())) allNaN = false;
      return allNaN ? 0 : 1;
    }
    case 2: {
      const n = r.u32();
      r.skip(n * dpp * 8);
      return n;
    }
    case 3: {
      const rings = r.u32();
      let sum = 0;
      for (let i = 0; i < rings; i += 1) {
        const np = r.u32();
        r.skip(np * dpp * 8);
        sum += np;
      }
      return sum;
    }
    case 4: {
      const n = r.u32();
      for (let i = 0; i < n; i += 1) walkWkbGeometry(r, h.ewkb); // consume; count = member count
      return n;
    }
    case 5:
    case 6:
    case 7: {
      const n = r.u32();
      let sum = 0;
      for (let i = 0; i < n; i += 1) sum += walkWkbGeometry(r, h.ewkb);
      return sum;
    }
    default:
      throw new Error('non-walkable geometry');
  }
}

function walkWkbGeometry(r: Reader, parentEwkb: boolean): number {
  return walkWkbBody(r, readWkbHeader(r, parentEwkb));
}

export type WkbFacts = { header: WkbHeader; geomType: SpatialGeomType | 'Unknown' };

export function factsFromWkbHeader(r: Reader, parentEwkb: boolean): WkbFacts | null {
  let header: WkbHeader;
  try {
    header = readWkbHeader(r, parentEwkb);
  } catch {
    return null;
  }
  const geomType = geomName(header.baseType);
  return geomType ? { header, geomType } : null;
}

export function optionalCount(walk: boolean, geomType: string, count: () => number): number | null {
  if (!walk || geomType === 'Unknown') return null;
  try {
    return count();
  } catch {
    return null;
  }
}

export function sniffWkb(bytes: Uint8Array, walk: boolean, byteLength: number): SniffFacts | null {
  const r = createReader(bytes);
  const f = factsFromWkbHeader(r, false);
  if (!f) return null;
  return {
    container: f.header.ewkb ? 'ewkb' : 'wkb',
    geomType: f.geomType,
    srid: f.header.srid,
    dimensions: dimsLabel(f.header.hasZ, f.header.hasM),
    pointCount: optionalCount(walk, f.geomType, () => walkWkbBody(r, f.header)),
    byteLength,
  };
}
